import { FC, useState } from 'react';

import {
  SendRefundLinkNoticeResult,
  sendRefundLinkNotice,
} from '@shared/api/giftCoupon/giftCoupon.controller';
import { giftCouponRefundRecipientUrl } from '@shared/utils/giftRecipientLink.util';

// ⚠️ 백엔드 기본 문구(DEFAULT_REFUND_LINK_NOTICE_MESSAGE)와 동일하게 유지할 것.
// 출처: back/smartbugo-api/src/gift-coupon/gift-coupon.refund-link-notice.ts
export const REFUND_LINK_NOTICE_DEFAULT_MESSAGE = [
  '[답례품 환불 안내]',
  '안녕하세요. 문의하신 답례품의 환불 신청 링크를 보내드립니다.',
  '',
  '아래 링크에서 본인 확인 후 환불받으실 계좌를 입력해 주시면',
  '영업일 기준 1~3일 안에 입금해 드립니다.',
].join('\n');

/** 환불 링크 안내 문자를 보낼 쿠폰 — 화면마다 쿠폰 타입이 달라 필요한 값만 받는다 */
export interface RefundLinkNoticeTarget {
  couponId: string;
  /** "#3 · ABC123" 식 표기 */
  label?: string;
  /** 교환 신청번호 (recipient.phoneNumber) */
  recipientPhone?: string;
  /** 확인번호 (additionalRecipientPhone) */
  additionalPhone?: string;
  /** 주문자 번호 */
  ordererPhone?: string;
  /** 이전 발송 기록 (issued.notice*) */
  lastNotice?: { sentAt?: Date | string; phone?: string; count?: number };
  /** 방금 링크를 발행하고 바로 연 경우 — 발행 완료 안내를 함께 보여준다 */
  justIssued?: boolean;
}

interface RefundLinkNoticeModalProps {
  target: RefundLinkNoticeTarget;
  /** sent: 이 모달에서 문자가 한 번이라도 나갔는지 — 부모가 목록 갱신 여부를 정한다 */
  onClose: (sent: boolean) => void;
}

const onlyDigits = (raw: string | undefined): string => (raw ?? '').replace(/\D/g, '');

const formatPhone = (raw: string | undefined): string => {
  const d = onlyDigits(raw);
  if (d.length === 11) return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
  return raw ?? '';
};

const isValidPhone = (digits: string) => digits.length === 10 || digits.length === 11;

// 테스트 발송 번호는 매번 같은 본인 번호라 이 브라우저에 기억해 둔다 (없어도 동작에 지장 없음)
const TEST_PHONE_STORAGE_KEY = 'giftRefundLinkNotice.testPhone';
const loadTestPhone = (): string => {
  try {
    return localStorage.getItem(TEST_PHONE_STORAGE_KEY) ?? '';
  } catch {
    return '';
  }
};
const saveTestPhone = (digits: string) => {
  try {
    localStorage.setItem(TEST_PHONE_STORAGE_KEY, digits);
  } catch {
    // 저장 실패는 무시 — 다음에 다시 입력하면 된다
  }
};

/** 대상·번호 검증 실패는 서버가 400 + 사유 문구로 준다 (그 외 상태는 문구를 쓸 수 없다) */
const sendErrorMessage = (err: unknown): string => {
  const response = (
    err as { response?: { status?: number; data?: { message?: unknown } } }
  )?.response;
  const serverMessage = response?.data?.message;
  if (response?.status === 404) {
    return '서버에 아직 반영되지 않은 기능입니다. (환불 링크 안내 문자 API 미배포)';
  }
  if (response?.status === 400 && typeof serverMessage === 'string' && serverMessage) {
    return serverMessage;
  }
  return '문자 발송에 실패했습니다.';
};

const formatSentAt = (value: Date | string | undefined): string => {
  if (!value) return '';
  return new Date(value).toLocaleString('ko-KR', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * 환불 링크 안내 문자 발송 모달.
 * 환불 링크 발행은 신청 페이지를 열어 줄 뿐 고객에게 알리지 않으므로, 링크와 안내 문구를
 * 답례품 발신번호 LMS로 보낸다. 링크는 서버가 본문 뒤에 항상 덧붙인다.
 *
 * 자주 쓰는 기능이 아니라, 고객에게 보내기 전에 [테스트 발송]으로 관리자 본인 번호에
 * 같은 문자를 먼저 받아 볼 수 있다 (발송 기록 없음).
 */
export const RefundLinkNoticeModal: FC<RefundLinkNoticeModalProps> = ({
  target,
  onClose,
}) => {
  const phoneOptions = [
    { label: '신청번호', phone: onlyDigits(target.recipientPhone) },
    { label: '확인번호', phone: onlyDigits(target.additionalPhone) },
    { label: '주문자', phone: onlyDigits(target.ordererPhone) },
  ].filter((o, idx, arr) => o.phone && arr.findIndex((x) => x.phone === o.phone) === idx);

  const [phone, setPhone] = useState(() => formatPhone(phoneOptions[0]?.phone));
  const [message, setMessage] = useState(REFUND_LINK_NOTICE_DEFAULT_MESSAGE);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SendRefundLinkNoticeResult | null>(null);

  // 테스트 발송 — 본인 번호로 같은 문자를 먼저 받아 본다
  const [showTest, setShowTest] = useState(false);
  const [testPhone, setTestPhone] = useState(() => formatPhone(loadTestPhone()));
  const [testSubmitting, setTestSubmitting] = useState(false);
  const [testSentPhone, setTestSentPhone] = useState<string | null>(null);

  const phoneDigits = onlyDigits(phone);
  const isPhoneValid = isValidPhone(phoneDigits);
  const testPhoneDigits = onlyDigits(testPhone);
  const isTestPhoneValid = isValidPhone(testPhoneDigits);
  const recipientDigits = onlyDigits(target.recipientPhone);
  const differsFromRecipient =
    isPhoneValid && !!recipientDigits && phoneDigits !== recipientDigits;
  const link = giftCouponRefundRecipientUrl(target.couponId);

  const handleSend = async () => {
    if (!isPhoneValid) {
      setError('수신번호를 확인해 주세요.');
      return;
    }
    if (!message.trim()) {
      setError('안내 문구를 입력해 주세요.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await sendRefundLinkNotice(target.couponId, {
        phoneNumber: phoneDigits,
        message: message.trim(),
        baseUrl: window.location.origin,
      });
      if (res.sent) setResult(res);
      else setError(`문자 발송 실패 (${res.reason ?? '사유 미상'})`);
    } catch (err: unknown) {
      setError(sendErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleTestSend = async () => {
    if (!isTestPhoneValid) {
      setError('테스트 번호를 확인해 주세요.');
      return;
    }
    if (!message.trim()) {
      setError('안내 문구를 입력해 주세요.');
      return;
    }
    setTestSubmitting(true);
    setError(null);
    setTestSentPhone(null);
    try {
      const res = await sendRefundLinkNotice(target.couponId, {
        phoneNumber: testPhoneDigits,
        message: message.trim(),
        baseUrl: window.location.origin,
        test: true,
      });
      if (res.sent) {
        saveTestPhone(testPhoneDigits);
        setTestSentPhone(res.phoneNumber);
      } else {
        setError(`테스트 발송 실패 (${res.reason ?? '사유 미상'})`);
      }
    } catch (err: unknown) {
      setError(sendErrorMessage(err));
    } finally {
      setTestSubmitting(false);
    }
  };

  const busy = submitting || testSubmitting;

  const handleClose = () => onClose(!!result);

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={handleClose} />
      <div className="relative flex max-h-[88vh] w-full max-w-md flex-col gap-4 overflow-y-auto rounded-xl bg-white p-5 shadow-2xl">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-base font-bold text-orange-800">환불 링크 문자 발송</h3>
            <p className="mt-0.5 text-xs text-gray-500">
              {target.label ? `${target.label} · ` : ''}환불 신청 링크와 안내 문구를 LMS로
              보냅니다.
            </p>
          </div>
          <button
            onClick={handleClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        {target.justIssued && !result && (
          <p className="rounded-lg bg-green-50 px-3 py-2 text-xs font-medium text-green-700">
            환불 링크 발행 완료 · 클립보드 복사됨
          </p>
        )}

        {!result ? (
          <>
            {target.lastNotice?.sentAt && (
              <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
                이전 발송 {target.lastNotice.count ?? 1}회 · 마지막{' '}
                {formatPhone(target.lastNotice.phone)} ·{' '}
                {formatSentAt(target.lastNotice.sentAt)}
              </p>
            )}

            {/* 수신번호 */}
            <div>
              <p className="mb-1.5 text-xs font-bold text-gray-700">수신번호</p>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="010-0000-0000"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm tabular-nums focus:border-orange-400 focus:outline-none"
              />
              {phoneOptions.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {phoneOptions.map((o) => (
                    <button
                      key={o.phone}
                      type="button"
                      onClick={() => setPhone(formatPhone(o.phone))}
                      className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${
                        phoneDigits === o.phone
                          ? 'border-orange-300 bg-orange-50 text-orange-700'
                          : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {o.label} {formatPhone(o.phone)}
                    </button>
                  ))}
                </div>
              )}
              {differsFromRecipient && (
                <p className="mt-1.5 text-[11px] text-amber-700">
                  신청번호와 다름 · 발송완료 건은 신청번호로만 본인 확인 가능
                </p>
              )}
            </div>

            {/* 안내 문구 */}
            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <p className="text-xs font-bold text-gray-700">안내 문구</p>
                {message !== REFUND_LINK_NOTICE_DEFAULT_MESSAGE && (
                  <button
                    type="button"
                    onClick={() => setMessage(REFUND_LINK_NOTICE_DEFAULT_MESSAGE)}
                    className="text-[11px] text-gray-500 underline hover:text-gray-700"
                  >
                    기본값으로
                  </button>
                )}
              </div>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={6}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm leading-relaxed focus:border-orange-400 focus:outline-none"
              />
              <div className="mt-1.5 rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-600">
                <p className="text-[11px] font-medium text-gray-400">문구 뒤 자동 첨부</p>
                <p className="mt-0.5">환불 신청하기</p>
                <p className="break-all text-indigo-600">{link}</p>
              </div>
            </div>

            {/* 테스트 발송 — 본인 번호로 먼저 받아 보기 (발송 기록 없음) */}
            {showTest && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <p className="text-xs font-bold text-gray-700">테스트 발송</p>
                <p className="mt-0.5 text-[11px] text-gray-500">
                  위 문구 그대로 내 번호로 먼저 받아 봅니다 · 발송 기록 없음
                </p>
                <div className="mt-2 flex gap-2">
                  <input
                    type="tel"
                    value={testPhone}
                    onChange={(e) => setTestPhone(e.target.value)}
                    placeholder="내 휴대폰 번호"
                    className="min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm tabular-nums focus:border-orange-400 focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={handleTestSend}
                    disabled={busy || !isTestPhoneValid}
                    className="shrink-0 rounded-lg border border-orange-300 bg-white px-3 py-2 text-sm font-bold text-orange-700 hover:bg-orange-50 disabled:opacity-50"
                  >
                    {testSubmitting ? '발송 중...' : '테스트 보내기'}
                  </button>
                </div>
                {testSentPhone && (
                  <p className="mt-2 text-[11px] font-medium text-green-700">
                    테스트 발송 완료 · {formatPhone(testSentPhone)} · 확인 후 [문자 발송]
                  </p>
                )}
              </div>
            )}

            {error && (
              <p className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-600">
                {error}
              </p>
            )}

            <div className="flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => setShowTest((v) => !v)}
                disabled={busy}
                className="text-xs font-medium text-gray-500 underline hover:text-gray-700 disabled:opacity-50"
              >
                {showTest ? '테스트 발송 접기' : '테스트 발송'}
              </button>
              <div className="flex gap-2">
                <button
                  onClick={handleClose}
                  disabled={busy}
                  className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  {target.justIssued ? '문자 없이 닫기' : '닫기'}
                </button>
                <button
                  onClick={handleSend}
                  disabled={busy || !isPhoneValid}
                  className="rounded-lg bg-orange-600 px-4 py-2 text-sm font-bold text-white hover:bg-orange-700 disabled:opacity-50"
                >
                  {submitting ? '발송 중...' : '문자 발송'}
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            <p className="rounded-lg bg-green-50 px-3 py-3 text-sm font-medium text-green-700">
              발송 완료 · {formatPhone(result.phoneNumber)}
            </p>
            <div className="flex justify-end">
              <button
                onClick={handleClose}
                className="rounded-lg bg-gray-800 px-4 py-2 text-sm font-bold text-white hover:bg-gray-900"
              >
                닫기
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
